import React, { useCallback, useEffect, useState } from "react";
import {
    CalendarOutlined,
    ProjectOutlined,
    ReloadOutlined,
    ArrowRightOutlined,
} from "@ant-design/icons";
import {
    Card,
    Col,
    Empty,
    Row,
    Skeleton,
    Space,
    Table,
    Tag,
    Typography,
} from "antd";
import { Client } from "../../core/api-client";
import { API_GET_DASHBOARD_SUMMARY_COUNTS } from "../../shared/routes/private.routes";
import DashboardLoadError from "../dashboard/components/dashboard-load-error.component";
import { isFailedResponse } from "../dashboard/dashboard-response";
import "../../assets/styles/dashboard.less";

const { Title, Text } = Typography;

type ProjectSowAlert = {
    projectId: string;
    projectName: string;
    l2ProjectName?: string;
    sowNumber?: string;
    poNumber?: string;
    accountName?: string;
    endDate: string;
    startDate:string;
    daysRemaining: number;
};

type ProjectSummary = {
    total: number;
    active: number;
    newProjects: number;
    inactive: number;
    draft: number;
};

type DashboardData = {
    project: ProjectSummary;
    alerts: {
        expiredProjectSow: ProjectSowAlert[];
        upcomingProjectSow: ProjectSowAlert[];
    };
};

const emptyDashboardData: DashboardData = {
    project: {
        total: 0,
        active: 0,
        newProjects: 0,
        inactive: 0,
        draft: 0,
    },
    alerts: {
        expiredProjectSow: [],
        upcomingProjectSow: [],
    },
};

const summaryItems = [
    { label: "Active", key: "active", color: "success" },
    { label: "New Projects (Last 30 days)", key: "newProjects", color: "processing" },
    { label: "Draft", key: "draft", color: "processing" },
    { label: "Inactive (Last 30 days)", key: "inactive", color: "default" },
] as const;

const ProjectDashboardPage: React.FC = () => {
    const [dashboardData, setDashboardData] = useState<DashboardData>(emptyDashboardData);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        setError(false);

        try {
            const response = await Client.get(API_GET_DASHBOARD_SUMMARY_COUNTS);
            if (isFailedResponse(response)) {
                setError(true);
                return;
            }
            const data = response?.data?.data;

            setDashboardData({
                project: {
                    ...emptyDashboardData.project,
                    ...(data?.project || {}),
                },
                alerts: {
                    ...emptyDashboardData.alerts,
                    ...(data?.alerts || {}),
                },
            });
        } catch (err) {
            console.error("Failed to load project dashboard:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadDashboard();
    }, [loadDashboard]);

    const project = dashboardData.project;
    const projectSowAlerts = [
        ...[...dashboardData.alerts.expiredProjectSow].sort((a, b) => b.endDate.localeCompare(a.endDate)),
        ...[...dashboardData.alerts.upcomingProjectSow].sort((a, b) => a.endDate.localeCompare(b.endDate)),
    ];

    return (
        <div className="app-page dashboard-page">
            <div className="app-page-header dashboard-page-header">
                <div>
                    <div className="app-page-title-wrap">
                        <span className="app-page-icon">
                            <ProjectOutlined />
                        </span>
                        <Title level={1} className="app-page-title">
                            Project Dashboard
                        </Title>
                    </div>
                    <p className="app-page-subtitle">Project overview</p>
                </div>

                <button
                    className="dashboard-refresh-button"
                    type="button"
                    onClick={() => void loadDashboard()}
                    disabled={loading}
                    aria-label="Refresh project dashboard"
                >
                    <ReloadOutlined />
                    <span>Refresh</span>
                </button>
            </div>

            {error ? (
                <DashboardLoadError
                    message="Unable to load project dashboard data."
                    onRetry={() => void loadDashboard()}
                />
            ) : (
            <>
            <Row gutter={[16, 16]}>
                <Col xs={24}>
                    <Card className="dashboard-summary-card" variant="borderless">
                        {loading ? (
                            <Skeleton active paragraph={{ rows: 3 }} />
                        ) : (
                            <>
                                <div className="dashboard-summary-top">
                                    <div className="dashboard-summary-main">
                                        <span className="dashboard-summary-icon">
                                            <ProjectOutlined />
                                        </span>
                                        <Text className="dashboard-summary-title">
                                            Total Projects
                                        </Text>
                                    </div>
                                    <div className="dashboard-summary-total">
                                        {project.total}
                                    </div>
                                </div>

                                <div className="dashboard-status-grid">
                                    {summaryItems.map((item) => (
                                        <div className="dashboard-status-item" key={item.key}>
                                            <Space size={8}>
                                                <Tag color={item.color}>{item.label}</Tag>
                                            </Space>
                                            <strong>{project[item.key]}</strong>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[16, 16]} className="dashboard-alert-grid">
                <Col xs={24}>
                    <ProjectSowAlertTable
                        title="Project / SOW Expiry"
                        description="Expired in the last 90 days or expiring in the next 30 days"
                        data={projectSowAlerts}
                        loading={loading}
                    />
                </Col>
            </Row>
            </>
            )}
        </div>
    );
};

type ProjectSowAlertTableProps = {
    title: string;
    description: string;
    data: ProjectSowAlert[];
    loading: boolean;
};

function ProjectSowAlertTable({
    title,
    description,
    data,
    loading,
}: ProjectSowAlertTableProps) {
    const [showAll, setShowAll] = useState(false);
    const columns = [
         {
            title: "PO Number",
            dataIndex: "poNumber",
            ellipsis: true,
            width: 250,
            render: (value: string) => value || "-",
        },
        {
            title: "Project / SOW",
            dataIndex: "projectName",
            ellipsis: true,
            render: (_: string, row: ProjectSowAlert) => (
                <div>
                    <strong>{row.l2ProjectName || row.projectName || "-"}</strong>
                    <div className="dashboard-muted-text">
                        SOW: {row.sowNumber || "-"}
                    </div>
                </div>
            ),
        },
        {
            title: "Client",
            dataIndex: "accountName",
            ellipsis: true,
            render: (value: string) => value || "-",
        },
        {
            title: "Start Date",
            dataIndex: "startDate",
            width: 120,
            render: (value: string) => formatDate(value),
        },
        {
            title: "End Date",
            dataIndex: "endDate",
            width: 120,
            render: (value: string) => formatDate(value),
        },
        {
            title: "Status",
            dataIndex: "daysRemaining",
            width: 110,
            render: (value: number) => <Tag color={value < 0 ? "error" : "warning"}>{value < 0 ? "Expired" : "Upcoming"}</Tag>,
        },
        {
            title: "Days",
            dataIndex: "daysRemaining",
            width: 150,
            render: (value: number) => (
                <Tag
                    color={value < 0 ? "error" : "warning"}
                    style={value < 0 ? { color: "var(--error)", fontWeight: 600 } : undefined}
                >
                    {Math.abs(value)} {value < 0 ? "days overdue" : "days left"}
                </Tag>
            ),
        },
    ];

    const visibleData = showAll ? data : data.slice(0, 5);

    return (
        <Card
            className="dashboard-alert-card"
            variant="borderless"
            title={
                <div className="dashboard-alert-title">
                    <div className="dashboard-alert-title-main">
                        <Space size={8}>
                            <CalendarOutlined />
                            <span>{title}</span>
                        </Space>
                        <span className="dashboard-alert-window">{description}</span>
                    </div>
                    {!loading && (
                        <Tag color="warning">
                            {data.length}
                        </Tag>
                    )}
                </div>
            }
        >
            <Table<ProjectSowAlert>
                size="small"
                loading={loading}
                rowKey={(row) => `${row.projectId}-${row.sowNumber || row.endDate}`}
                dataSource={visibleData}
                columns={columns}
                pagination={false}
                tableLayout="fixed"
                scroll={{ x: 760 }}
                locale={{
                    // antd still shows emptyText under the spinner when dataSource is [].
                    emptyText: loading ? null : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="No projects found"
                        />
                    ),
                }}
            />

            {data.length > 5 && (
                <button
                    type="button"
                    className="dashboard-view-all-button"
                    onClick={() => setShowAll((value) => !value)}
                >
                    {showAll ? "Show less" : "View all"}
                    <ArrowRightOutlined />
                </button>
            )}
        </Card>
    );
}

function formatDate(value?: string) {
    if (!value) return "-";

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;

    return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
}

export default ProjectDashboardPage;
